import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Slider,
  Box,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  getPersonById,
  getSystemSimilarityThreshold,
  updatePersonSimilarityThreshold,
} from '../utils/api';

const primary = '#40518A';
const FALLBACK_SYSTEM_DEFAULT = 0.42;

const SLIDER_MARKS = [
  { value: 0, label: '0' },
  { value: 0.25, label: '0.25' },
  { value: 0.5, label: '0.5' },
  { value: 0.75, label: '0.75' },
  { value: 1, label: '1' },
];

const hasStoredThreshold = (value) => (
  value !== null && value !== undefined && value !== ''
);

const SimilarityThresholdDialog = ({
  open,
  onClose,
  person,
  onSaved,
  showSnackbar,
}) => {
  const [value, setValue] = useState(FALLBACK_SYSTEM_DEFAULT);
  const [useSystemDefault, setUseSystemDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [storedThreshold, setStoredThreshold] = useState(null);
  const [systemDefaultThreshold, setSystemDefaultThreshold] = useState(FALLBACK_SYSTEM_DEFAULT);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const res = await getSystemSimilarityThreshold();
        const parsed = parseFloat(res?.similarity_threshold);
        if (!cancelled && Number.isFinite(parsed)) {
          setSystemDefaultThreshold(parsed);
        }
      } catch (_) {
        // Keep fallback default when settings endpoint is unavailable.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !person?.person_id) {
      setStoredThreshold(null);
      return undefined;
    }

    let cancelled = false;

    const applyThreshold = (raw) => {
      if (hasStoredThreshold(raw)) {
        const parsed = parseFloat(raw);
        setStoredThreshold(parsed);
        setUseSystemDefault(false);
        setValue(Number.isFinite(parsed) ? parsed : FALLBACK_SYSTEM_DEFAULT);
      } else {
        setStoredThreshold(null);
        setUseSystemDefault(true);
        setValue(systemDefaultThreshold);
      }
    };

    applyThreshold(person.similarity_threshold);

    (async () => {
      setLoading(true);
      try {
        const fresh = await getPersonById(person.person_id);
        if (!cancelled && fresh) {
          applyThreshold(fresh.similarity_threshold);
        }
      } catch (_) {
        // Keep values from the person object passed into the dialog.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, person]);

  useEffect(() => {
    if (useSystemDefault) {
      setValue(systemDefaultThreshold);
    }
  }, [useSystemDefault, systemDefaultThreshold]);

  const handleSystemDefaultChange = (checked) => {
    setUseSystemDefault(checked);
    if (checked) {
      setValue(systemDefaultThreshold);
      return;
    }
    if (hasStoredThreshold(storedThreshold)) {
      setValue(storedThreshold);
    }
  };

  const handleSave = async () => {
    if (!person?.person_id) return;
    setSaving(true);
    try {
      const payload = useSystemDefault ? null : parseFloat(value);
      await updatePersonSimilarityThreshold(person.person_id, payload);
      if (showSnackbar) {
        showSnackbar(
          useSystemDefault
            ? 'Similarity threshold reset to system default'
            : `Similarity threshold set to ${payload.toFixed(2)}`,
          'success'
        );
      }
      if (onSaved) {
        onSaved(person.person_id, useSystemDefault ? null : payload);
      }
      onClose();
    } catch (err) {
      if (showSnackbar) {
        showSnackbar(err.message || 'Failed to update similarity threshold', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const storedLabel = hasStoredThreshold(storedThreshold)
    ? Number(storedThreshold).toFixed(2)
    : 'System default (not set)';

  const sliderLabel = useSystemDefault
    ? `Threshold (default): ${parseFloat(systemDefaultThreshold).toFixed(2)}`
    : `Threshold: ${parseFloat(value || 0).toFixed(2)}`;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set similarity threshold</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {person?.name ? (
            <>
              Match threshold for <strong>{person.name}</strong> (person ID: {person.person_id}).
              Used during live recognition instead of the global default when set.
            </>
          ) : (
            'Per-person match threshold for live recognition.'
          )}
        </Typography>
        <Box
          sx={{
            mb: 2,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            bgcolor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            Current stored threshold
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {loading ? 'Loading…' : storedLabel}
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={useSystemDefault}
              onChange={(e) => handleSystemDefaultChange(e.target.checked)}
              disabled={saving || loading}
            />
          }
          label="Use default Similarity Threshold"
        />
        <Box sx={{ px: 1, pt: 2 }}>
          <Typography variant="body2" gutterBottom>
            {sliderLabel}
          </Typography>
          <Slider
            value={useSystemDefault ? systemDefaultThreshold : parseFloat(value || systemDefaultThreshold)}
            min={0}
            max={1}
            step={0.01}
            marks={SLIDER_MARKS}
            onChange={(_, v) => setValue(v)}
            disabled={saving || loading || useSystemDefault}
            valueLabelDisplay="auto"
            sx={{
              color: primary,
              '& .MuiSlider-markLabel': {
                fontSize: '0.75rem',
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
          sx={{ backgroundColor: primary }}
        >
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimilarityThresholdDialog;
